import React from 'react';
import {ForecastMapService} from '../api';
import {ForecastMapConfig} from '../api/types';
import useApiClient from './useApiClient';

export type ForecastMapCatalogStatus = 'loading' | 'ready' | 'error';

type CatalogResult = {
  // Which load produced this result (reload count and include_locked); a
  // mismatch means a load is in flight.
  key: string | null;
  status: 'ready' | 'error';
  configs: ForecastMapConfig[];
};

/**
 * Fetches the forecast-map preset catalog (all models — grouping happens
 * client-side so switching models never refetches).
 *
 * `includeLocked` also lists the presets this account cannot open, marked
 * `locked: true` (#327 MR7a). Only the unified map asks for them: every
 * other consumer would offer configs whose manifests 404.
 *
 * Platform-agnostic on purpose (no DOM usage): backs the webapp screens and
 * the mobile forecast-map screen.
 */
const useForecastMapCatalog = ({
  includeLocked = false,
}: {includeLocked?: boolean} = {}) => {
  const {fetch} = useApiClient();
  const [loadKey, setLoadKey] = React.useState(0);
  const [result, setResult] = React.useState<CatalogResult>({
    key: null,
    status: 'ready',
    configs: [],
  });

  const key = `${loadKey}:${includeLocked}`;
  React.useEffect(() => {
    let cancelled = false;
    fetch(
      ForecastMapService.forecastMapConfigList(
        includeLocked ? {include_locked: true} : undefined,
      ),
    )
      .then((configs) => {
        if (!cancelled) {
          setResult({key, status: 'ready', configs});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({key, status: 'error', configs: []});
        }
      });
    // Drop late responses on unmount/reload.
    return () => {
      cancelled = true;
    };
  }, [fetch, key, includeLocked]);

  const reload = React.useCallback(() => setLoadKey((count) => count + 1), []);

  // Derived: any load whose result hasn't landed yet renders as loading.
  const loading = result.key !== key;
  const status: ForecastMapCatalogStatus = loading ? 'loading' : result.status;

  return {status, configs: loading ? [] : result.configs, reload};
};

export default useForecastMapCatalog;
